//src/components/AuthProvider.tsx
import React from "react";
import { useAuth } from "../auth/AuthProvider";
import { trackEvent } from "../lib/analytics";


type Props = { open: boolean; onClose: () => void };

export default function AuthModal({ open, onClose }: Props) {
  const { user, signIn, signUp, sendPasswordReset, signInWithGoogle, signInWithGitHub } = useAuth();

  const [mode, setMode] = React.useState<"login" | "signup" | "forgot">("login");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  

  React.useEffect(() => {
    if (open) {
      // whenever modal is opened from the header, show the login view by default
      setMode("login");
      setMessage(null);
      setPassword("");
    }
  }, [open]);

  // Auto-close when a session appears (covers the normal flow + edge cases)
  React.useEffect(() => {
    if (!open) return;
    if (user) {
      setLoading(false);
      setPassword("");
      setMessage(null);
      setMode("login");
      onClose();
    }
  }, [user, open, onClose]); // <-- important

  if (!open) return null;

  const withTimeout = <T,>(
    promise: Promise<T>,
    milliseconds = 15000
  ): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        window.setTimeout(() => {
          reject(
            new Error(
              "Authentication request timed out. Check your Supabase URL, API key, and browser console."
            )
          );
        }, milliseconds);
      }),
    ]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      if (mode === "login") {
        await withTimeout(signIn(email, password));
        // success → close immediately
        onClose();
      } else if (mode === "signup") {
        await withTimeout(signUp(email, password));

        trackEvent("sign_up", {
          method: "email",
        });

        // either close now, or show a message if you require email confirmation:
        setMessage("Account created. Check your email if confirmation is required.");
        onClose(); // remove this line if you prefer to keep the modal open after sign-up
      } else if (mode === "forgot") {
        await withTimeout(sendPasswordReset(email));
        setMessage("Reset link sent. Check your email.");
        setMode("login");
        // keep modal open so they can log in after clicking email, or close if you prefer:
        // onClose();
      }
    } catch (err: any) {
      setMessage(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
      <div className="w-full max-w-md rounded-xl bg-white text-black p-5 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">
            {mode === "login"  && "Sign in"} 
            {mode === "signup" && "Create account"}
            {mode === "forgot" && "Reset password"}
          </h2>
          <button onClick={onClose} className="text-gray-500">✕</button>
        </div>

        {message && (
          <div className="mb-3 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded p-2">
            {message}
          </div>
        )}

        <button
          type="button"
          onClick={async () => {
            setLoading(true);
            setMessage(null);

            try {
              await signInWithGoogle();

              trackEvent(mode === "signup" ? "sign_up" : "login", {
                method: "google",
              });
            } catch (err: any) {
              setMessage(err?.message || "Google sign-in failed. Please try again.");
              setLoading(false);
            }
          }}
          className="w-full flex items-center justify-center gap-4 rounded-md border border-gray-300 bg-white py-3 font-medium text-gray-900 hover:bg-gray-50 transition"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
            className="w-6 h-6"
          >
            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.655 32.657 29.195 36 24 36c-6.627 0-12-5.373-12-12S17.373 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.27 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
            <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 16.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.27 4 24 4c-7.682 0-14.318 4.337-17.694 10.691z"/>
            <path fill="#4CAF50" d="M24 44c5.167 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.145 35.091 26.715 36 24 36c-5.174 0-9.627-3.326-11.283-7.946l-6.522 5.025C9.538 39.556 16.227 44 24 44z"/>
            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-1.101 3.109-3.449 5.588-6.493 6.57l.005-.003 6.19 5.238C34.567 40.091 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
          </svg>
          {mode === "signup" ? "Sign up with Google" : "Continue with Google"}
        </button>

        <button
          type="button"
          onClick={async () => {
            setLoading(true);
            setMessage(null);

            try {
              await signInWithGitHub();

              trackEvent(mode === "signup" ? "sign_up" : "login", {
                method: "github",
              });
              
            } catch (err: any) {
              setMessage(err?.message || "GitHub sign-in failed. Please try again.");
              setLoading(false);
            }
          }}
          className="mt-3 w-full flex items-center justify-center gap-4 rounded-md border border-gray-300 bg-white py-3 font-medium text-gray-900 hover:bg-gray-50 transition"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            className="w-6 h-6"
            fill="currentColor"
          >
            <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.09 3.29 9.4 7.86 10.93.58.11.79-.25.79-.56v-2.02c-3.2.7-3.87-1.36-3.87-1.36-.53-1.34-1.29-1.7-1.29-1.7-1.05-.72.08-.71.08-.71 1.16.08 1.77 1.19 1.77 1.19 1.04 1.77 2.72 1.26 3.38.96.1-.75.4-1.26.73-1.55-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.29 1.19-3.1-.12-.29-.52-1.46.11-3.05 0 0 .98-.31 3.2 1.18A11.1 11.1 0 0 1 12 6.15c.99 0 1.99.13 2.92.37 2.22-1.49 3.19-1.18 3.19-1.18.64 1.59.24 2.76.12 3.05.74.81 1.18 1.84 1.18 3.1 0 4.43-2.69 5.4-5.25 5.68.41.36.77 1.06.77 2.14v3.16c0 .31.21.67.8.56A11.51 11.51 0 0 0 23.5 12C23.5 5.65 18.35.5 12 .5Z" />
          </svg>
          {mode === "signup" ? "Sign up with GitHub" : "Continue with GitHub"}
        </button>

        <div className="my-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-gray-300" />
          <span className="text-xs text-gray-600 font-medium">OR CONTINUE WITH EMAIL</span>
          <div className="h-px flex-1 bg-gray-300" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm mb-1">Email address</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="you@example.com"        // ← no duplicate label text
              autoComplete="email"
            />
          </div>
          {mode !== "forgot" && (
            <div className="space-y-1">
              <label className="text-sm">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border rounded px-3 py-2 pr-10"
                  placeholder="••••••••"
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "🙈" : "👁️"}
                </button>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg border border-border bg-panel text-text hover:shadow-soft disabled:opacity-60 py-2"
          >
            {loading
              ? "Please wait…"
              : mode === "signup"
              ? "Create account"
              : mode === "forgot"
              ? "Send reset link"
              : "Sign in"}
          </button>
        </form>

        <div className="mt-3 text-sm flex items-center justify-between">
          {mode === "login" && (
            <>
              <button
                className="text-sm text-subtext hover:underline" 
                onClick={() => setMode("forgot")}
              >
                Forgot your password?
              </button>
              <button
                className="text-sm text-subtext hover:underline" 
                onClick={() => setMode("signup")}
              >
                Create new account
              </button>
            </>
          )}
          {mode !== "login" && (
            <button className="text-gray-700 underline" onClick={() => setMode("login")}>
              Back to login
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
